import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Injectable, Injector, OnDestroy, Type, inject } from '@angular/core';
import { WIZY_DIALOG_DATA, WizyDialogConfig, WizyDialogRef } from './wizy-dialog.model';

/**
 * Service de dialogue compatible avec MatDialog
 * Utilise Angular CDK Overlay pour créer des dialogues dynamiquement
 *
 * @example
 * ```typescript
 * constructor(private dialog: WizyDialogService) {}
 *
 * openDialog() {
 *   const dialogRef = this.dialog.open(MyComponent, {
 *     data: { name: 'John' },
 *     size: 'md'
 *   });
 *
 *   dialogRef.afterClosed().subscribe(result => {
 *     console.log('Dialog closed with:', result);
 *   });
 * }
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class WizyDialogService implements OnDestroy {
    private readonly overlay = inject(Overlay);
    private readonly injector = inject(Injector);
    private readonly focusTrapFactory = inject(ConfigurableFocusTrapFactory);

    // Map pour stocker les dialogues ouverts
    private openDialogs = new Map<string, { dialogRef: WizyDialogRef<any, any>; overlayRef: OverlayRef; focusTrap: ConfigurableFocusTrap }>();
    private dialogCounter = 0;

    /**
     * Ouvre un dialogue avec le composant spécifié
     *
     * @param component - Le composant à afficher dans le dialogue
     * @param config - Configuration du dialogue
     * @returns Une référence au dialogue ouvert
     */
    open<T, D = any, R = any>(component: Type<T>, config?: WizyDialogConfig<D>): WizyDialogRef<T, R> {
        const dialogId = `wizy-dialog-${this.dialogCounter++}`;

        // Configuration par défaut
        const dialogConfig: WizyDialogConfig<D> = {
            size: 'md',
            type: 'default',
            hasHeader: true,
            hasFooter: false,
            disableClose: true,
            ...config,
        };

        // Créer la configuration de l'overlay
        const overlayConfig = this.getOverlayConfig(dialogConfig);
        const overlayRef = this.overlay.create(overlayConfig);

        // Créer la référence du dialogue avec l'overlayRef
        const dialogRef = new WizyDialogRef<T, R>(() => {
            this.closeDialog(dialogId);
        }, overlayRef);

        // Appliquer disableClose
        dialogRef.disableClose = dialogConfig.disableClose ?? true;

        // Appliquer panelClass si spécifié (sera maintenant appliqué directement sur l'overlay)
        if (dialogConfig.panelClass) {
            dialogRef.addPanelClass(dialogConfig.panelClass);
        }

        // Créer un injecteur avec les données du dialogue et la référence
        const dialogInjector = Injector.create({
            parent: this.injector,
            providers: [
                { provide: WIZY_DIALOG_DATA, useValue: dialogConfig.data },
                { provide: WizyDialogRef, useValue: dialogRef },
            ],
        });

        // Créer le portal avec le composant
        const portal = new ComponentPortal(component as ComponentType<T>, null, dialogInjector);

        // Attacher le composant à l'overlay
        overlayRef.attach(portal);

        // Piéger le focus dans la modale pour éviter les interactions avec les éléments derrière
        const focusTrap = this.focusTrapFactory.create(overlayRef.overlayElement);
        focusTrap.focusInitialElementWhenReady();

        // Stocker la référence
        this.openDialogs.set(dialogId, { dialogRef, overlayRef, focusTrap });

        // Gérer le clic sur le backdrop
        if (!dialogConfig.disableClose) {
            overlayRef.backdropClick().subscribe(() => {
                dialogRef.close();
            });
        }

        // Gérer la touche Escape
        if (!dialogConfig.disableClose) {
            overlayRef.keydownEvents().subscribe((event) => {
                if (event.key === 'Escape') {
                    dialogRef.close();
                }
            });
        }

        return dialogRef;
    }

    /**
     * Ferme tous les dialogues ouverts
     */
    closeAll(): void {
        this.openDialogs.forEach(({ dialogRef }) => {
            dialogRef.close();
        });
        this.openDialogs.clear();
    }

    /**
     * Retourne le dialogue actuellement ouvert (s'il y en a un)
     */
    getOpenDialog<T = any, R = any>(): WizyDialogRef<T, R> | undefined {
        const dialogEntries = Array.from(this.openDialogs.values());
        const lastEntry = dialogEntries[dialogEntries.length - 1];
        return lastEntry?.dialogRef as WizyDialogRef<T, R> | undefined;
    }

    /**
     * Ferme un dialogue spécifique
     */
    private closeDialog(dialogId: string): void {
        const dialogEntry = this.openDialogs.get(dialogId);
        if (dialogEntry) {
            // Détruire le focus trap
            dialogEntry.focusTrap.destroy();

            // Détruire le dialogue et nettoyer les références
            dialogEntry.dialogRef.destroy();

            // Détacher et disposer de l'overlay
            dialogEntry.overlayRef.detach();
            dialogEntry.overlayRef.dispose();
        }
        this.openDialogs.delete(dialogId);
    }

    /**
     * Crée la configuration de l'overlay
     */
    private getOverlayConfig(config: WizyDialogConfig): OverlayConfig {
        const positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();

        // Déterminer si on est en mode fullscreen
        const panelClasses = config.panelClass ? (Array.isArray(config.panelClass) ? config.panelClass : [config.panelClass]) : [];
        const isFullscreen = panelClasses.includes('fullscreen-dialog');

        // Pour le mode fullscreen, convertir les pourcentages en viewport units
        const width = isFullscreen && config.width === '100%' ? '100vw' : config.width;
        const height = isFullscreen && config.height === '100%' ? '100vh' : config.height;
        const maxWidth = isFullscreen && config.maxWidth === '100vw' ? undefined : config.maxWidth;
        const maxHeight = isFullscreen && config.maxHeight === '100vh' ? undefined : config.maxHeight;

        return new OverlayConfig({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'wizy-dialog-backdrop',
            panelClass: ['wizy-dialog-panel', this.getSizeClass(config.size), ...panelClasses],
            scrollStrategy: this.overlay.scrollStrategies.block(),
            width,
            height,
            maxWidth,
            maxHeight,
        });
    }

    /**
     * Retourne la classe CSS pour la taille du dialogue
     */
    private getSizeClass(size?: string): string {
        switch (size) {
            case 'sm':
                return 'wizy-dialog-sm';
            case 'lg':
                return 'wizy-dialog-lg';
            case 'full':
                return 'wizy-dialog-full';
            case 'md':
            default:
                return 'wizy-dialog-md';
        }
    }

    /**
     * Nettoie tous les dialogues ouverts lors de la destruction du service
     * Prévient les memory leaks
     */
    ngOnDestroy(): void {
        this.closeAll();
    }
}
